import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { TextSnippet as TextSnippetIcon, GetApp as GetAppIcon } from "@mui/icons-material";
import CardTable from "components/CardTable/CardTable.jsx";
import SelectDateMenu from "components/SelectDateMenu/SelectDateMenu";
import Snackbar from "components/Snackbar/Snackbar.js";
import { ModalV2 } from "components/Modal/ModalV2";
import { MapComponent } from "./MapComponent";
import RegularButton from "components/CustomButtons/Button";
import { convertToMapById } from "helpers/convertToMap";
import { exportExcelFile } from "helpers/exportExcelFile";
import { formatCustomDate } from "helpers/dateTools";
import { formatterNumberDecimals } from "helpers/mathDictionary";
import { useSearch } from "hooks/useSearch";
import { columns } from "./columns";
import { getSupplyNotes, closeModal } from "redux/actions/supplyNotes/suplyNotesActions";

export const SupplyNotes = () => {
  const dispatch = useDispatch();
  const { supplyNotes, supplyNote, openModal } = useSelector((state) => state.supplyNotes);
  const { stationsSelected, startDate, endDate, color, message, isOpen, isLoading } = useSelector((state) => state.search);
  const { stations } = useSelector(state => state.companies);
  const [mapStations, setMapStations] = useState([]);
  const [mapSupplyNotes, setMapSupplyNotes] = useState([]);
  const {
    changeStation,
    changeStartDate,
    changeEndDate,
    setSnackbar,
    clearSnackbar,
  } = useSearch();

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const SupplyNotesHeaders = {
    unitName: "Unidad",
    folio: "Nota",
    customer: "Cliente",
    quantity: "Litros",
    initialdate: "Fecha inicio servicio",
    initialLocation: "Ubicación inicial",
    finalDate: "Fecha fin servicio",
    finalLocation: "Ubicación final",
    minutesDiff: "Diferencia minutos",
    distanceDiff: "Diferencia metros",
    status: "Estatus Metros",
  };

  const mapSupplyNotesDownload = (notes) => {
    return notes && Object.values(notes).map((value) => ({
      unitName: value.unitName || "Sin registro",
      folio: value.folio || "Sin registro",
      customer: value.customer || "Sin registro",
      quantity: value.quantity || "Sin registro",
      initialdate: value.initialdate || "Sin registro",
      initialLocation: value.initialLocation || "Sin registro",
      finalDate: value.finalDate || "Sin registro",
      finalLocation: value.finalLocation || "Sin registro",
      minutesDiff: value.minutesDiff || "Sin registro",
      distanceDiff: formatterNumberDecimals(value.distanceDiff, 2) || "0",
      status: value.status || "Sin registro",
    }));
  };

  const handleDownLoad = () => {
    const fileName = `NotasSurtido-${stationsSelected.name}-${formatCustomDate(startDate)}-${formatCustomDate(endDate)}.xlsx`;
    exportExcelFile(mapSupplyNotesDownload(mapSupplyNotes), fileName, SupplyNotesHeaders);
  };

  const handleSubmit = () => {
    if (stationsSelected.length < 1) {
      setSnackbar("No se ha seleccionado alguna planta.");
      return;
    }
    const currentDay = dayjs();

    if (!startDate) {
      setSnackbar("No se ha seleccionado fecha de inicio.");
      return;
    }
    if (!endDate) {
      setSnackbar("No se ha seleccionado fecha final.");
      return;
    }
    if (!dayjs(startDate).isSameOrBefore(dayjs(endDate), "day")) {
      setSnackbar("La fecha final no puede ser menor a la inicial.");
      return;
    }
    if (dayjs(endDate).diff(dayjs(currentDay), "day") >= 1) {
      setSnackbar("La fecha final no puede ser mayor a un dia del dia actual");
      return;
    }
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    dispatch(getSupplyNotes(stationsSelected.id, start, end));
  };

  useEffect(() => {
    setMapStations(convertToMapById(stations));
    if (supplyNotes) {
      setMapSupplyNotes(Object.values(supplyNotes).map(value => ({
        ...value,
        initialLocation: `${value.initialLatitude}, ${value.initialLongitude}`,
        finalLocation: `${value.finalLatitude}, ${value.finalLongitude}`,
        status: value.distanceDiff > 120 ? "Muy mala" : value.distanceDiff > 80 ? "Mala" :
          value.distanceDiff > 40 ? "Regular" : "Buena",
      })));
    }
  }, [stations, supplyNotes]);

  return (
    <>
      <SelectDateMenu
        onChangeStartDate={changeStartDate}
        onChangeEndDate={changeEndDate}
        dates={{ startDate, endDate }}
        color="info"
        hasStations
        stations={mapStations}
        optionSelected={stationsSelected}
        onChangeFilter={changeStation}
        disabled={isLoading}
        onSubmit={handleSubmit}
        isMultiOne={false}
      />
      <CardTable
        columns={columns}
        data={mapSupplyNotes}
        isLoading={isLoading}
        hasControl
        hasHeader
        icon={<TextSnippetIcon />}
        title="Ubicación Notas de Surtido"
        titleBlock
        blockBtn
        pageSize={15}
        controls={
          <RegularButton
            onClick={handleDownLoad}
            color="info"
            justIcon
            round
            tooltip="Descargar"
            disabled={Object.keys(mapSupplyNotes).length === 0}
          >
            <GetAppIcon />
          </RegularButton>
        }
      />
      <Snackbar
        open={isOpen}
        place="tr"
        message={message}
        color={color}
        closeNotification={clearSnackbar}
        close
      />
      <ModalV2
        open={openModal}
        title="Mapa"
        onClose={handleCloseModal}
        isMap
      >
        <MapComponent supplyNote={supplyNote} />
      </ModalV2>
    </>
  );
};
